import { useFormContext, useWatch } from "react-hook-form";
import Input, { InputType } from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import { SubAppCode } from "utils/engine/consts";
import Radio from "components/shared/inputs/radio";
import { useEffect, useRef } from "react";
import { EtudeFormFields } from "../constants";

export const ExpertModeFields: React.FC<{
  allData: any;
  calculate: any;
  autoRecalc: boolean;
  setCallCalculate2?: (val: boolean) => void;
}> = ({ allData, calculate, autoRecalc, setCallCalculate2 }) => {
  const { t } = useTranslation();
  const { control, setValue, getFieldState, getValues } =
    useFormContext<EtudeFormFields>();
  const subapplication = useWatch({ name: "subApplication" });
  const expertModeType = useWatch({ name: "expertModeType" });

  const prevValues = useRef<Partial<EtudeFormFields>>(getValues());
  let prevExpertModeType = useRef<string>("");

  const onTypeRadioChange = (e: React.MouseEvent<HTMLInputElement>) => {
    setValue("expertModeType", (e.target as HTMLInputElement).value, {
      shouldDirty: true,
    });

    setCallCalculate2?.(true);
  };

  const didMountRef = useRef(false);

  const handleRecalcEvent =
    <T extends keyof EtudeFormFields>(field: T) =>
    (
      e:
        | React.FocusEvent<HTMLInputElement>
        | React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (!autoRecalc) return;

      const isEnter = (e as React.KeyboardEvent).key === "Enter";
      const isBlur = e.type === "blur";

      if (!isEnter && !isBlur) return;

      const current = getValues(field);
      const prev = prevValues.current[field];

      if (current !== prev) {
        calculate();
        setCallCalculate2?.(true);
        prevValues.current[field] = current;
      }
    };

  const recalcEvents = <T extends keyof EtudeFormFields>(field: T) => ({
    onBlur: handleRecalcEvent(field),
    onKeyDown: handleRecalcEvent(field),
  });

  const handleSelectChange = <T extends keyof EtudeFormFields>(
    value: any,
    name: T
  ) => {
    setValue(name, value, { shouldDirty: true, shouldValidate: true });
    if (autoRecalc) {
      calculate();
    }
  };

  const isAppartement = expertModeType === "appartement";

  useEffect(() => {
    if (getFieldState("subApplication").isDirty) {
      setValue("expertModeType", "appartement");
    }
  }, [subapplication]);

  useEffect(() => {
    if (!autoRecalc) return;
    const current = getValues("expertModeType");

    if (didMountRef.current) {
      if (prevExpertModeType.current !== current) {
        calculate();
      }
    } else {
      didMountRef.current = true;
    }
    prevExpertModeType.current = current!;
  }, [expertModeType, autoRecalc]);

  switch (subapplication) {
    case SubAppCode.LOGEMENTSPRIVES:
    case SubAppCode.LOGEMENTSSOCIAUX:
      return (
        <group data-direction="column" data-gap="10">
          <text>{t("consommation_journaliere_maximale")}</text>
          <label>
            <Radio
              control={control}
              name="expertModeType"
              radioValue="appartement"
              checked={expertModeType === "appartement"}
              onClick={onTypeRadioChange}
              label={t("par_type_dappartement")}
            />
          </label>
          <group data-gap="10">
            <group data-direction="column" data-gap="10" data-length="autofit">
              <Input
                type="number"
                name="consoT1"
                unit="liters_a_60_c"
                control={control}
                label="t1"
                disabled={!isAppartement}
                minValue={allData?.consoT1?.minimum}
                maxValue={allData?.consoT1?.maximum}
                {...recalcEvents("consoT1")}
              />
              <Input
                type="number"
                name="consoT2"
                unit="liters_a_60_c"
                control={control}
                label="t2"
                disabled={!isAppartement}
                minValue={allData?.consoT2?.minimum}
                maxValue={allData?.consoT2?.maximum}
                {...recalcEvents("consoT2")}
              />
              <Input
                type="number"
                name="consoT3"
                unit="liters_a_60_c"
                control={control}
                label="t3"
                disabled={!isAppartement}
                minValue={allData?.consoT3?.minimum}
                maxValue={allData?.consoT3?.maximum}
                {...recalcEvents("consoT3")}
              />
            </group>
            <group data-direction="column" data-gap="10" data-length="autofit">
              <Input
                type="number"
                name="consoT4"
                unit="liters_a_60_c"
                control={control}
                label="t4"
                disabled={!isAppartement}
                minValue={allData?.consoT4?.minimum}
                maxValue={allData?.consoT4?.maximum}
                {...recalcEvents("consoT4")}
              />
              <Input
                type="number"
                name="consoT5"
                unit="liters_a_60_c"
                control={control}
                label="t5"
                disabled={!isAppartement}
                minValue={allData?.consoT5?.minimum}
                maxValue={allData?.consoT5?.maximum}
                {...recalcEvents("consoT5")}
              />
              <Input
                type="number"
                name="consoT6"
                unit="liters_a_60_c"
                control={control}
                label="t6"
                disabled={!isAppartement}
                minValue={allData?.consoT6?.minimum}
                maxValue={allData?.consoT6?.maximum}
                {...recalcEvents("consoT6")}
              />
            </group>
          </group>
          <label>
            <Radio
              control={control}
              name="expertModeType"
              radioValue="batiment"
              checked={expertModeType === "batiment"}
              onClick={onTypeRadioChange}
              label={t("pour_le_batiment")}
            />
          </label>
          <group data-gap="10">
            <group data-direction="column" data-gap="10" data-length="autofit">
              <Input
                type="number"
                name="consoBatiment"
                disabled={isAppartement}
                unit="liters_a_60_c"
                control={control}
                label=""
                minValue={allData?.consoBatiment?.minimum}
                maxValue={allData?.consoBatiment?.maximum}
                {...recalcEvents("consoBatiment" as keyof EtudeFormFields)}
              />
            </group>
            <group
              data-direction="column"
              data-gap="10"
              data-length="autofit"
            ></group>
          </group>
        </group>
      );
    case SubAppCode.RESIDENCESSENIORS:
      return (
        <group data-direction="column" data-gap="10">
          <label>
            <Radio
              control={control}
              name="consommationJournaliereMaximaleParLit"
              radioValue="appartement"
              checked={expertModeType !== "batiment"}
              onClick={onTypeRadioChange}
              label={t("par_logement")}
            />
          </label>
          <Input
            control={control}
            unit="liters_a_60_c"
            type="number"
            name="consoT1"
            disabled={!isAppartement}
            minValue={allData?.consoT1?.minimum}
            maxValue={allData?.consoT1?.maximum}
            {...recalcEvents("consoT1")}
          />
          <text>{t("ou")}</text>
          <label>
            <Radio
              control={control}
              name="consommationJournaliereMaximaleParLit"
              radioValue="batiment"
              checked={expertModeType === "batiment"}
              onClick={onTypeRadioChange}
              label={t("pour_le_batiment")}
            />
          </label>
          <Input
            control={control}
            unit="liters_a_60_c"
            type="number"
            name="consoBatiment"
            disabled={isAppartement}
            minValue={allData?.consoBatiment?.minimum}
            maxValue={allData?.consoBatiment?.maximum}
            {...recalcEvents("consoBatiment" as keyof EtudeFormFields)}
          />
        </group>
      );
    case SubAppCode.RESIDENCESETUDIANTE:
    case SubAppCode.FOYERDEJEUNESTRAVAILLEURS:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="volume_journalier_maximal_consomme_par_lit_a_60_c"
            unit="liters_a_60_c"
            type={InputType.Select}
            options={allData?.Cjpl?.enum}
            name="Cjpl"
            onChangeCustom={(val) =>
              handleSelectChange(val, "Cjpl" as keyof EtudeFormFields)
            }
          />
          <text>{t("repas")}</text>
          <Input
            control={control}
            label="consommation_ecs_par_couvert"
            unit="liters_a_60_c"
            type={InputType.Select}
            options={allData?.ECSbyCouv?.enum}
            name="ECSbyCouv"
            onChangeCustom={(val) =>
              handleSelectChange(val, "ECSbyCouv" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_pointe_de_restauration"
            unit="min"
            type={InputType.Select}
            options={allData?.tRestau?.enum}
            name="tRestau"
            onChangeCustom={(val) =>
              handleSelectChange(val, "tRestau" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="simultaneite_du_besoin_ecs_restauration_avec_le_besoin_des_chambres"
            type={InputType.Select}
            options={allData?.Simult?.enum}
            name="Simult"
            helpText={t("FO_HELP_SIMUL")}
            onChangeCustom={(val) =>
              handleSelectChange(val, "Simult" as keyof EtudeFormFields)
            }
          />
        </group>
      );
    case SubAppCode.CAMPING:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="duree_daffluence_au_bloc_sanitaire"
            type={InputType.Select}
            options={allData?.PfB?.enum}
            name="PfB"
            onChangeCustom={(val) =>
              handleSelectChange(val, "PfB" as keyof EtudeFormFields)
            }
          />

          <Input
            control={control}
            label="douches"
            type="number"
            name="NbD"
            disabled={true}
            minValue={allData?.NbD?.minimum}
            maxValue={allData?.NbD?.maximum}
            {...recalcEvents("NbD" as keyof EtudeFormFields)}
          />
          <Input control={control} label="lavabos" type="number" name="NbL" />
          <Input
            control={control}
            label="bacs_a_laver_la_vaisselle"
            type="number"
            name="NbBV"
            minValue={allData?.NbBV?.minimum}
            maxValue={allData?.NbBV?.maximum}
            {...recalcEvents("NbBV" as keyof EtudeFormFields)}
          />
          <Input
            control={control}
            label="bacs_a_laver_la_linge"
            type="number"
            name="NbBL"
            minValue={allData?.NbBL?.minimum}
            maxValue={allData?.NbBL?.maximum}
            {...recalcEvents("NbBL" as keyof EtudeFormFields)}
          />
        </group>
      );
    case SubAppCode.AFFAIRES:
    case SubAppCode.MONTAGNE:
    case SubAppCode.TOURISME:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="consommation_journaliere_par_chambre"
            unit="liters_a_60_c"
            type={InputType.Select}
            options={allData?.Cjpc?.enum}
            name="Cjpc"
            onChangeCustom={(val) =>
              handleSelectChange(val, "Cjpc" as keyof EtudeFormFields)
            }
          />
          {t("repas")}
          <Input
            control={control}
            label="consommation_ecs_par_couvert"
            unit="liters_a_60_c_couvert"
            type={InputType.Select}
            options={allData?.ECSbyCouv?.enum}
            name="ECSbyCouv"
            onChangeCustom={(val) =>
              handleSelectChange(val, "ECSbyCouv" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_pointe_de_restauration"
            unit="min"
            type={InputType.Select}
            options={allData?.tRestau?.enum}
            name="tRestau"
            onChangeCustom={(val) =>
              handleSelectChange(val, "tRestau" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="simultaneite_du_besoin_ecs_restauration_avec_le_besoin_des_chambres"
            type={InputType.Select}
            options={allData?.Simult?.enum}
            name="Simult"
            helpText={t("FO_HELP_SIMUL")}
            onChangeCustom={(val) => handleSelectChange(val, "Simult")}
          />
        </group>
      );
    case SubAppCode.STADEFOOTBALL:
    case SubAppCode.STADERUGBY:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="debit_nominal_d_une_douche"
            unit="l_min"
            type={InputType.Select}
            options={allData?.QDch?.enum}
            name="QDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "QDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_ecoulement_d_une_douche"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsEcDch?.enum}
            name="TpsEcDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsEcDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_occupation_d_une_douche_par_personne"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsTotDch?.enum}
            name="TpsTotDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsTotDch" as keyof EtudeFormFields)
            }
          />
        </group>
      );
    case SubAppCode.CASERNE /*TODO: CASERNE SAME AS STADEFOOTBALL STADERUGBY*/ /*NOSONAR*/:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="debit_nominal_d_une_douche"
            unit="l_min"
            type={InputType.Select}
            options={allData?.QDch?.enum}
            name="QDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "QDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_ecoulement_d_une_douche"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsEcDch?.enum}
            name="TpsEcDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsEcDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_occupation_d_une_douche_par_personne"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsTotDch?.enum}
            name="TpsTotDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsTotDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_de_pointe"
            unit="min"
            type="number"
            name="PointeModif"
            minValue={allData?.PointeModif?.minimum}
            maxValue={allData?.PointeModif?.maximum}
            {...recalcEvents("PointeModif" as keyof EtudeFormFields)}
          />
        </group>
      );
    case SubAppCode.VESTIAIREDENTREPRISE:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="debit_nominal_d_une_douche"
            unit="l_min"
            type={InputType.Select}
            options={allData?.QDch?.enum}
            name="QDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "QDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_ecoulement_d_une_douche"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsEcDch?.enum}
            name="TpsEcDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsEcDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_occupation_d_une_douche_par_personne"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsTotDch?.enum}
            name="TpsTotDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsTotDch" as keyof EtudeFormFields)
            }
          />
        </group>
      );
    case SubAppCode.STATIONDELAVAGE:
      return (
        <group data-direction="column" data-gap="10">
          {t("cycle_complet")}
          <Input
            control={control}
            label="duree_lavage_brosse__eau_chaude"
            unit="min_s"
            type="number"
            name="TpsBrosse"
            minValue={allData?.TpsBrosse?.minimum}
            maxValue={allData?.TpsBrosse?.maximum}
            {...recalcEvents("TpsBrosse" as keyof EtudeFormFields)}
          />
          <Input
            control={control}
            label="duree_lavage_hp__eau_chaude"
            unit="min_s"
            type="number"
            name="TpsHp"
            minValue={allData?.TpsHp?.minimum}
            maxValue={allData?.TpsHp?.maximum}
            {...recalcEvents("TpsHp" as keyof EtudeFormFields)}
          />
          <Input
            control={control}
            label="duree_rincage_finition_eau_froide"
            unit="min_s"
            type="number"
            name="TpsRinc"
            minValue={allData?.TpsRinc?.minimum}
            maxValue={allData?.TpsRinc?.maximum}
            {...recalcEvents("TpsRinc" as keyof EtudeFormFields)}
          />
          <Input
            control={control}
            label="duree_sans_puisage_entre_2_vehicules"
            unit="min_s"
            type="number"
            name="TpsSSPuis"
            minValue={allData?.TpsSSPuis?.minimum}
            maxValue={allData?.TpsSSPuis?.maximum}
            {...recalcEvents("TpsSSPuis" as keyof EtudeFormFields)}
          />
          <Input
            control={control}
            label="duree_de_la_periode_daffluence"
            unit="h"
            type={InputType.Select}
            options={allData?.NbPuisages?.enum}
            name="NbPuisages"
            onChangeCustom={(val) => handleSelectChange(val, "NbPuisages")}
          />
          <Input
            //TODO: change name
            name="period"
            control={control}
            label="nombre_de_vehicules_par_periode_daffluence"
            type="number"
            disabled={true}
            minValue={allData?.period?.minimum}
            maxValue={allData?.period?.maximum}
            {...recalcEvents("period" as keyof EtudeFormFields)}
          />
        </group>
      );
    case SubAppCode.INTERNAT:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="debit_nominal_d_une_douche"
            unit="l_min"
            type={InputType.Select}
            options={allData?.QDch?.enum}
            name="QDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "QDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_ecoulement_d_une_douche"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsEcDch?.enum}
            name="TpsEcDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsEcDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_occupation_d_une_douche_par_personne"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsTotDch?.enum}
            name="TpsTotDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsTotDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="consommation_journaliere_par_lit"
            unit="la_60_C"
            type="number"
            name="ConsoJourLit"
            minValue={allData?.ConsoJourLit?.minimum}
            maxValue={allData?.ConsoJourLit?.maximum}
            {...recalcEvents("ConsoJourLit" as keyof EtudeFormFields)}
          />
        </group>
      );
    case SubAppCode.GYMNASE:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="debit_nominal_d_une_douche"
            unit="l_min"
            type={InputType.Select}
            options={allData?.QDch?.enum}
            name="QDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "QDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_ecoulement_d_une_douche"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsEcDch?.enum}
            name="TpsEcDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsEcDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_occupation_d_une_douche_par_personne"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsTotDch?.enum}
            name="TpsTotDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsTotDch" as keyof EtudeFormFields)
            }
          />
        </group>
      );
    case SubAppCode.PISCINE:
    case SubAppCode.SALLEDEFITNESS:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="periode_d_ouverture_d_acces_aux_bassins"
            unit="h"
            type={InputType.Select}
            options={allData?.NbPuisages?.enum}
            name="NbPuisages"
            onChangeCustom={(val) => handleSelectChange(val, "NbPuisages")}
          />
          <Input
            control={control}
            label="periode_d_affluence"
            unit="h"
            type="number"
            name="TpsAffl"
            minValue={allData?.TpsAffl?.minimum}
            maxValue={allData?.TpsAffl?.maximum}
            {...recalcEvents("TpsAffl" as keyof EtudeFormFields)}
          />
          <text style={{ textDecorationLine: "underline" }}>
            {t("fréquentation_horaire")}
          </text>
          <Input
            control={control}
            label="periode_d_affluence"
            unit="pers_h"
            type="number"
            name="Affl"
            helpText={t("FO_HELP_SWIM_FITNESS_PA")}
            minValue={allData?.Affl?.minimum}
            maxValue={allData?.Affl?.maximum}
            {...recalcEvents("Affl" as keyof EtudeFormFields)}
          />
          <Input
            control={control}
            label="periode_hors_affluence"
            unit="pers_h"
            type="number"
            name="NAffl"
            disabled={true}
            helpText={t("FO_HELP_SWIM_FITNESS_PHA")}
            minValue={allData?.NAffl?.minimum}
            maxValue={allData?.NAffl?.maximum}
            {...recalcEvents("NAffl" as keyof EtudeFormFields)}
          />
          <Input
            control={control}
            label="debit_nominal_d_une_douche"
            unit="l_min"
            type={InputType.Select}
            options={allData?.QDch?.enum}
            name="QDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "QDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_ecoulement_d_une_douche"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsEcDch?.enum}
            name="TpsEcDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsEcDch" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_d_occupation_d_une_douche_par_personne"
            unit="min"
            type={InputType.Select}
            options={allData?.TpsTotDch?.enum}
            name="TpsTotDch"
            onChangeCustom={(val) =>
              handleSelectChange(val, "TpsTotDch" as keyof EtudeFormFields)
            }
          />
        </group>
      );
    case SubAppCode.HOPITALCLINIQUEDOUCHES:
    case SubAppCode.EHPAD:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            unit="liters_a_60_c"
            label="volume_journalier_maximal_consomme_par_lit_a_60_c"
            type="number"
            name="VJRef"
            minValue={allData?.VJRef?.minimum}
            maxValue={allData?.VJRef?.maximum}
            {...recalcEvents("VJRef" as keyof EtudeFormFields)}
          />
          <Input
            name="ecs_par_repas"
            control={control}
            unit="liters_a_60_c"
            label="volume_ecs_par_repas_hors_lita_titre_dinformation"
            type="number"
            value={4}
            disabled={true}
            {...recalcEvents("ecs_par_repas" as keyof EtudeFormFields)}
          />
        </group>
      );
    case SubAppCode.TRADITIONNEL:
    case SubAppCode.GASTRONOMIQUE:
    case SubAppCode.RAPIDE:
    case SubAppCode.COLLECTIF:
      return (
        <group data-direction="column" data-gap="10">
          <Input
            control={control}
            label="consommation_decs_par_repas"
            unit="l_a_60_c_repas"
            type={InputType.Select}
            options={allData?.ECSbyRepas?.enum}
            name="ECSbyRepas"
            onChangeCustom={(val) =>
              handleSelectChange(val, "ECSbyRepas" as keyof EtudeFormFields)
            }
          />
          <Input
            control={control}
            label="duree_de_pointe_de_consommation_ecs"
            unit="min"
            type={InputType.Select}
            options={allData?.tRestau?.enum}
            name="tRestau"
            onChangeCustom={(val) =>
              handleSelectChange(val, "tRestau" as keyof EtudeFormFields)
            }
          />
        </group>
      );

    default:
      return null;
  }
};
